/**
 * EnsureUserOnLogin — store-user-on-first-login bridge.
 *
 * Once Convex reports the WorkOS session as authenticated, calls the
 * `users.ensureUser` mutation exactly once to upsert the app `users` row
 * (keyed by the WorkOS user id). This guarantees requireUser/
 * requireWorkspaceMember can resolve the caller without depending on the
 * WorkOS webhook or backfill — and covers the first-login race in production.
 *
 * Renders its children unchanged; it only fires the side-effect.
 */
import { useEffect, useRef } from "react";
import { useConvexAuth, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";

export function EnsureUserOnLogin({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { isAuthenticated } = useConvexAuth();
  const ensureUser = useMutation(api.users.ensureUser);
  const ranForSession = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // Reset so a future re-login re-runs the upsert.
      ranForSession.current = false;
      return;
    }
    if (ranForSession.current) return;
    ranForSession.current = true;
    void ensureUser({}).catch(() => {
      // Allow a retry on the next auth tick if it failed.
      ranForSession.current = false;
    });
  }, [isAuthenticated, ensureUser]);

  return <>{children}</>;
}
